import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { apiServices } from '../../../services/apiServices';
import Swal from 'sweetalert2';

// Configure SweetAlert2 to have higher z-index
Swal.mixin({
  customClass: {
    container: 'swal2-container-high-z'
  }
});

const CategoryManagement = () => {
  // Add CSS for high z-index SweetAlert
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .swal2-container-high-z {
        z-index: 99999 !important;
      }
      .swal2-container {
        z-index: 99999 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal states
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Form states for creating category
  const [formData, setFormData] = useState({
    categoryname: '',
    status: 'UNPAID',
    remark: ''
  });
  const [creating, setCreating] = useState(false);

  // Fetch categories from API
  const fetchCategories = async (pageNum = 0, limit = 10) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pageNum + 1,
        limit: limit
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      // Add sorting by ID ascending
      params.append('sortBy', 'categoryid');
      params.append('sortOrder', 'ASC');

      const response = await apiServices.getAPI(`categories?${params.toString()}`);

      if (response.data.success) {
        const data = response.data.data || {};
        const pagination = data.pagination || {};
        const safeCount =
          (typeof pagination.total_count === 'number' && isFinite(pagination.total_count)) ? pagination.total_count :
          (typeof data.total_count === 'number' && isFinite(data.total_count)) ? data.total_count :
          (typeof data.count === 'number' && isFinite(data.count)) ? data.count :
          (Array.isArray(data.categories) ? data.categories.length : 0);

        setCategories(Array.isArray(data.categories) ? data.categories : []);
        setTotalCount(safeCount);
      } else {
        setError('Failed to fetch categories');
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Error fetching categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setPage(0);
    fetchCategories(0, rowsPerPage);
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPage(0);
    fetchCategories();
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setFormData({
      categoryname: '',
      status: 'UNPAID',
      remark: ''
    });
    setCreateModalOpen(true);
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setFormData({
      categoryname: '',
      status: 'UNPAID',
      remark: ''
    });
  };

  // Open status update modal
  const handleOpenStatusModal = (category) => {
    setSelectedCategory(category);
    setNewStatus(category.status);
    setStatusModalOpen(true);
  };

  // Close status modal
  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedCategory(null);
    setNewStatus('');
  };

  // Handle create category
  const handleCreateCategory = async () => {
    if (!formData.categoryname.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Category name is required',
        icon: 'error',
        allowOutsideClick: false,
        allowEscapeKey: true,
        backdrop: true,
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    if (!formData.status) {
      Swal.fire({
        title: 'Error',
        text: 'Status is required',
        icon: 'error',
        allowOutsideClick: false,
        allowEscapeKey: true,
        backdrop: true,
        customClass: {
          container: 'swal2-container-high-z'
        }
      });
      return;
    }

    setCreating(true);
    try {
      const adminDataStr = localStorage.getItem('adminData');
      const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;
      const username = adminData?.username || 'admin';

      const response = await apiServices.postAPI('category', {
        categoryname: formData.categoryname.trim(),
        status: formData.status,
        remark: formData.remark.trim(),
        createdby: username
      });

      if (response.data.success) {
        Swal.fire({
          title: 'Success',
          text: 'Category created successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          allowOutsideClick: false,
          allowEscapeKey: true,
          backdrop: true,
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
        handleCloseCreateModal();
        fetchCategories(page, rowsPerPage);
      } else {
        throw new Error(response.data.message || 'Failed to create category');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      
      // Handle 409 error for duplicate category name
      if (err.response && err.response.status === 409) {
        const errorData = err.response.data;
        if (errorData && errorData.error === "Category name already exists") {
          const existingCategory = errorData.existingCategory;
          Swal.fire({
            title: 'Category Already Exists',
            html: `
              <div style="text-align: left;">
                <p><strong>Error:</strong> ${errorData.error}</p>
                <p><strong>Existing Category:</strong></p>
                <ul style="margin: 10px 0; padding-left: 20px;">
                  <li><strong>Name:</strong> ${existingCategory?.categoryname || 'N/A'}</li>
                </ul>
                <p style="color: #666; font-size: 14px; margin-top: 15px;">
                  Please choose a different category name.
                </p>
              </div>
            `,
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#214F7D',
            allowOutsideClick: false,
            allowEscapeKey: true,
            backdrop: true,
            customClass: {
              container: 'swal2-container-high-z'
            }
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: errorData?.error || 'Category name already exists',
            icon: 'error',
            allowOutsideClick: false,
            allowEscapeKey: true,
            backdrop: true,
            customClass: {
              container: 'swal2-container-high-z'
            }
          });
        }
      } else {
        // Handle other errors
        Swal.fire({
          title: 'Error',
          text: err.message || 'Failed to create category',
          icon: 'error',
          allowOutsideClick: false,
          allowEscapeKey: true,
          backdrop: true,
          customClass: {
            container: 'swal2-container-high-z'
          }
        });
      }
    } finally {
      setCreating(false);
    }
  };

  // Handle update payment status
  const handleUpdateStatus = async () => {
    if (!newStatus) {
      Swal.fire({
        title: 'Error',
        text: 'Please select a status',
        icon: 'error',
        zIndex: 9999
      });
      return;
    }

    if (newStatus === selectedCategory.status) {
      Swal.fire({
        title: 'Info',
        text: 'Status is already set to ' + newStatus,
        icon: 'info',
        zIndex: 9999
      });
      return;
    }

    setUpdatingStatus(true);
    try {
      const adminDataStr = localStorage.getItem('adminData');
      const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;
      const username = adminData?.username || 'admin';

      const response = await apiServices.putAPI(`category/${selectedCategory.categoryid}/status`, {
        status: newStatus,
        updatedby: username
      });

      if (response.data.success) {
        Swal.fire({
          title: 'Success',
          text: 'Payment status updated successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          zIndex: 9999
        });
        handleCloseStatusModal();
        fetchCategories(page, rowsPerPage);
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to update payment status',
        icon: 'error',
        zIndex: 9999
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle export categories
  const handleExportCategories = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Starting export of all categories...');

      // Fetch all categories
      const params = new URLSearchParams({
        page: 1,
        limit: 100
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await apiServices.getAPI(`categories?${params.toString()}`);

      if (!response.data.success) {
        setError('Failed to fetch categories for export');
        return;
      }

      const allCategories = response.data.data.categories;
      console.log(`Exporting ${allCategories.length} categories...`);

      // Download CSV file
      downloadCSV(allCategories, 'categories_export.csv');

      console.log('Export completed successfully');
    } catch (err) {
      console.error('Error exporting categories:', err);
      setError('Error exporting categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Convert categories to CSV format
  const convertCategoriesToCSV = (categories) => {
    if (!categories || categories.length === 0) {
      return [];
    }

    const headers = [
      'Category ID',
      'Category Name',
      'Status',
      'Remark',
      'Created By',
      'Created Date',
      'Updated By',
      'Updated Date'
    ];

    const dataRows = categories.map(cat => [
      cat.categoryid || '',
      cat.categoryname || '',
      cat.status || '',
      cat.remark || '',
      cat.createdby || '',
      cat.created_dt ? new Date(cat.created_dt).toLocaleDateString() : '',
      cat.updatedby || '',
      cat.updated_dt ? new Date(cat.updated_dt).toLocaleDateString() : ''
    ]);

    return [headers, ...dataRows];
  };

  // Download CSV file
  const downloadCSV = (categories, filename) => {
    try {
      const csvData = convertCategoriesToCSV(categories);

      const csvContent = csvData.map(row =>
        row.map(cell => {
          const cellStr = String(cell).replace(/"/g, '""');
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr}"`;
          }
          return cellStr;
        }).join(',')
      ).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      console.log(`CSV file ${filename} downloaded successfully`);
    } catch (error) {
      console.error('Error creating CSV file:', error);
      setError('Error creating CSV file. Please try again.');
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    return status?.toUpperCase() === 'PAID' ? 'success' : 'warning';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch categories when dependencies change
  useEffect(() => {
    fetchCategories(page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchCategories(0, rowsPerPage);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, rowsPerPage]);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', pr: 4, mr: 4}}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mr: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create and manage categories and their payment status
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateModal}
          sx={{
            backgroundColor: '#3A8139',
            color: '#fff',
            '&:hover': { backgroundColor: '#2d6329' },
            px: 3,
            py: 1,
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Add New Category
        </Button>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mr:2 }}>
        <CardContent sx={{ py: 2, px: 3 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by category name..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6c757d' }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#214F7D',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#214F7D',
                    },
                  },
                }}
              />
            </Grid>

            {/* <Grid item xs={12} md={3}>
              <FormControl size="small" sx={{ minWidth: '120px' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiSelect-select': { paddingRight: '32px' }
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="PAID">Paid</MenuItem>
                  <MenuItem value="UNPAID">Unpaid</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  onClick={handleApplyFilters}
                  size="small"
                  sx={{
                    backgroundColor: '#214F7D',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#1a3f63' },
                    px: 2, py: 1, borderRadius: '6px',
                    textTransform: 'none', fontWeight: 500, minWidth: '80px',
                  }}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCategories}
                  size="small"
                  disabled={categories.length === 0}
                  sx={{
                    borderColor: '#28a745', color: '#28a745',
                    '&:hover': { borderColor: '#1e7e34', backgroundColor: 'rgba(40, 167, 69, 0.04)' },
                    px: 2, py: 1, borderRadius: '6px',
                    textTransform: 'none', fontWeight: 500, minWidth: '80px',
                  }}
                >
                  Export
                </Button>
                <Tooltip title="Refresh">
                  <IconButton onClick={handleRefresh} size="small"
                    sx={{
                      border: '1px solid #dee2e6', backgroundColor: '#fff', width: '40px',
                      height: '40px',
                      '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#214F7D' },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: '1.1rem', color: '#6c757d' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Categories Table */}
      <Paper sx={{ width: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'auto',  pr: 2, mr: 2 }}>
        <TableContainer sx={{ maxHeight: 500, overflowX: 'auto', overflowY: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                {/* <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 100 }}>ID</TableCell> */}
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 200 }}>Category Name</TableCell>
                {/* <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 100 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 200 }}>Remark</TableCell> */}
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Created Date</TableCell>
                {/* <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Actions</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      Loading categories...
                    </Box>
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No categories found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.categoryid} hover sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    {/* <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: '0.875rem' }}>
                        #{category.categoryid}
                      </Typography>
                    </TableCell> */}
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {category.categoryname}
                      </Typography>
                    </TableCell>
                    {/* <TableCell sx={{ py: 1 }}>
                      <Chip
                        label={category.status}
                        color={getStatusColor(category.status)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          '& .MuiChip-label': { fontSize: '0.75rem' }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                        {category.remark || 'N/A'}
                      </Typography>
                    </TableCell> */}
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {category.createdby || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatDate(category.created_dt)}
                      </Typography>
                    </TableCell>
                    {/* <TableCell align="center" sx={{ pr: 3 }}>
                      <Tooltip title="Update Payment Status">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenStatusModal(category)}
                          sx={{
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell> */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Summary */}
      <Box sx={{
        mt: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          <strong>Total Categories:</strong> {totalCount} | <strong>Showing:</strong> {categories.length} of {totalCount}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
        </Typography>
      </Box>

      {/* Update Status Modal */}
      <Dialog
        open={statusModalOpen}
        onClose={handleCloseStatusModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 2,
          px: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
            Update Payment Status
          </Typography>
          {selectedCategory && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {selectedCategory.categoryname}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pb: 2, pt: '32px !important', px: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Payment Status *</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Payment Status *"
              required
            >
              <MenuItem value="PAID">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4caf50' }} />
                  <Typography>PAID</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="UNPAID">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ff9800' }} />
                  <Typography>UNPAID</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {selectedCategory && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Current Status:</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={selectedCategory.status}
                  color={getStatusColor(selectedCategory.status)}
                  size="small"
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 4,
          py: 3,
          pt: 3,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Button
            onClick={handleCloseStatusModal}
            disabled={updatingStatus}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={updatingStatus || !newStatus}
            startIcon={updatingStatus ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{
              minWidth: 120,
              backgroundColor: '#214F7D',
              '&:hover': {
                backgroundColor: '#1a3f63'
              }
            }}
          >
            {updatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Category Modal */}
      <Dialog
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 3,
          pt: 4,
          px: 4,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
            Create New Category
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 2, pt: '32px !important', px: 4 }}>
          <Grid container spacing={3}>
            {/* Category Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={formData.categoryname}
                onChange={(e) => setFormData(prev => ({ ...prev, categoryname: e.target.value }))}
                required
                placeholder="Enter category name"
                helperText="Required: Unique name for this category"
              />
            </Grid>

            {/* Status */}
            {/* <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status *</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  label="Status *"
                  required
                >
                  <MenuItem value="PAID">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50' }} />
                      Paid
                    </Box>
                  </MenuItem>
                  <MenuItem value="UNPAID">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ff9800' }} />
                      Unpaid
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            {/* Remark */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remark"
                value={formData.remark}
                onChange={(e) => setFormData(prev => ({ ...prev, remark: e.target.value }))}
                placeholder="Enter remarks or description (optional)"
                helperText="Optional: Additional information about this category"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          px: 4,
          py: 3,
          pt: 3,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Button
            onClick={handleCloseCreateModal}
            disabled={creating}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCategory}
            variant="contained"
            disabled={creating || !formData.categoryname.trim() || !formData.status}
            startIcon={creating ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{
              minWidth: 140,
              backgroundColor: '#214F7D',
              '&:hover': {
                backgroundColor: '#1a3f63'
              }
            }}
          >
            {creating ? 'Creating...' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;

